import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './coverageApi';

/**
 * The dashboard was built as a static prototype. This provider puts the
 * coverage API in front of it without changing that: when the API answers,
 * every wired widget shows real ingested data; when it does not, the original
 * demo numbers are served instead and the UI is visually identical apart from
 * a "Demo data" badge.
 */

const CoverageContext = createContext(null);

/** Derives the sidebar's BRD list from real sources + their requirements. */
function deriveBrdDocuments(sources, requirements) {
  return sources
    .filter((s) => s.kind === 'brd')
    .map((s) => {
      const own = requirements.filter((r) => r.sourceId === s.id);
      const covered = own.filter((r) => r.status === 'Covered').length;
      const coveragePct = own.length ? Math.round((covered / own.length) * 100) : 0;

      const moduleCounts = new Map();
      for (const r of own) moduleCounts.set(r.module, (moduleCounts.get(r.module) ?? 0) + 1);
      const dominantModule =
        [...moduleCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Unassigned';

      return {
        id: s.id,
        name: s.name,
        requirements: own.length,
        coveragePct,
        status: coveragePct >= 70 ? 'Covered' : coveragePct >= 40 ? 'Partial' : 'Missing',
        module: dominantModule,
      };
    });
}

export function CoverageProvider({ fallback, children }) {
  const [state, setState] = useState({
    live: false,
    loading: true,
    error: null,
    ...fallback,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [summary, sourceSummary, requirementPage, sourceList] = await Promise.all([
        api.coverageSummary(),
        api.sourceSummary(),
        api.requirements(),
        api.sources(),
      ]);

      // An API that is up but has ingested nothing would blank the dashboard.
      // Keep the demo numbers until there is something real to show.
      if (summary.totalRequirements === 0) {
        setState({
          live: false,
          loading: false,
          error: 'No requirements ingested yet — showing demo data.',
          ...fallback,
        });
        return;
      }

      setState({
        live: true,
        loading: false,
        error: null,
        summary,
        sources: sourceSummary.sources,
        requirements: requirementPage.items,
        brdDocuments: deriveBrdDocuments(sourceList.sources, requirementPage.items),
      });
    } catch (err) {
      setState({
        live: false,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        ...fallback,
      });
    }
  }, [fallback]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo(() => ({ ...state, refresh: () => void load() }), [state, load]);

  return <CoverageContext.Provider value={value}>{children}</CoverageContext.Provider>;
}

export function useCoverage() {
  const ctx = useContext(CoverageContext);
  if (!ctx) throw new Error('useCoverage must be used inside <CoverageProvider>');
  return ctx;
}
